import { useState, useCallback } from 'react';
import { residentApi } from '../services/residentApi';
import { PropertyWithDetails } from '../../crm/components/properties/types';
import { LeaseWithDetails, CreatePhoneRequest, UpdatePhoneRequest, CreateEmailRequest, UpdateEmailRequest, CreateAddressRequest, UpdateAddressRequest } from '../types';
import { ContactWithDetails, Phone, Email, ContactsAddressWithDetails } from '../../crm/components/contacts/types';
import { useLoadingState } from '../../hooks/useLoadingState';

export const useResidentData = () => {
  const [property, setProperty] = useState<PropertyWithDetails | null>(null);
  const [lease, setLease] = useState<LeaseWithDetails | null>(null);
  const [contact, setContact] = useState<ContactWithDetails | null>(null);
  const [phones, setPhones] = useState<Phone[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [addresses, setAddresses] = useState<ContactsAddressWithDetails[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);

  const fetchProperty = useCallback(async () => {
    startLoading('fetchProperty', 'Loading property...');
    try {
      const response = await residentApi.getProperty();
      // API returns PropertyWithDetails directly or { data: PropertyWithDetails }
      setProperty((response as any).data || response || null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setProperty(null);
      } else {
        setError(err.message || 'Failed to fetch property');
      }
    } finally {
      stopLoading('fetchProperty');
    }
  }, [startLoading, stopLoading]);

  const fetchLease = useCallback(async () => {
    startLoading('fetchLease', 'Loading lease...');
    try {
      const response = await residentApi.getLease();
      // API returns LeaseWithDetails directly or { data: LeaseWithDetails }
      setLease((response as any).data || response || null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setLease(null);
      } else {
        setError(err.message || 'Failed to fetch lease');
      }
    } finally {
      stopLoading('fetchLease');
    }
  }, [startLoading, stopLoading]);

  const fetchContact = useCallback(async () => {
    startLoading('fetchContact', 'Loading contact info...');
    try {
      const response = await residentApi.getContact();
      // API returns ContactWithDetails directly or { data: ContactWithDetails }
      setContact((response as any).data || response || null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setContact(null);
      } else {
        setError(err.message || 'Failed to fetch contact info');
      }
    } finally {
      stopLoading('fetchContact');
    }
  }, [startLoading, stopLoading]);

  const fetchPhones = useCallback(async () => {
    startLoading('fetchPhones', 'Loading phones...');
    try {
      const data = await residentApi.getPhones();
      setPhones(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch phones');
    } finally {
      stopLoading('fetchPhones');
    }
  }, [startLoading, stopLoading]);

  const createPhone = useCallback(async (phoneData: CreatePhoneRequest) => {
    startLoading('createPhone', 'Adding phone...');
    try {
      const result = await residentApi.createPhone({
        typeId: phoneData.typeId,
        phoneNumber: phoneData.phoneNumber,
        isPrimary: Boolean(phoneData.isPrimary)
      });
      const newPhone = result.data;
      setPhones(prev => [...prev, newPhone]);
      return newPhone;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add phone');
      throw err;
    } finally {
      stopLoading('createPhone');
    }
  }, [startLoading, stopLoading]);

  const updatePhone = useCallback(async (id: number, phoneData: UpdatePhoneRequest) => {
    startLoading('updatePhone', 'Updating phone...');
    try {
      const result = await residentApi.updatePhone(id, phoneData);
      const updatedPhone = result.data;
      setPhones(prev => prev.map(p => (p.id === id ? updatedPhone : p)));
      return updatedPhone;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update phone');
      throw err;
    } finally {
      stopLoading('updatePhone');
    }
  }, [startLoading, stopLoading]);

  const deletePhone = useCallback(async (id: number) => {
    startLoading('deletePhone', 'Deleting phone...');
    try {
      await residentApi.deletePhone(id);
      setPhones(prev => prev.filter(p => p.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete phone');
      throw err;
    } finally {
      stopLoading('deletePhone');
    }
  }, [startLoading, stopLoading]);

  const fetchEmails = useCallback(async () => {
    startLoading('fetchEmails', 'Loading emails...');
    try {
      const data = await residentApi.getEmails();
      setEmails(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch emails');
    } finally {
      stopLoading('fetchEmails');
    }
  }, [startLoading, stopLoading]);

  const createEmail = useCallback(async (emailData: CreateEmailRequest) => {
    startLoading('createEmail', 'Adding email...');
    try {
      const result = await residentApi.createEmail({
        typeId: emailData.typeId,
        emailAddress: emailData.emailAddress,
        isPrimary: Boolean(emailData.isPrimary)
      });
      const newEmail = result.data;
      setEmails(prev => [...prev, newEmail]);
      return newEmail;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add email');
      throw err;
    } finally {
      stopLoading('createEmail');
    }
  }, [startLoading, stopLoading]);

  const updateEmail = useCallback(async (id: number, emailData: UpdateEmailRequest) => {
    startLoading('updateEmail', 'Updating email...');
    try {
      const result = await residentApi.updateEmail(id, emailData);
      const updatedEmail = result.data;
      setEmails(prev => prev.map(e => (e.id === id ? updatedEmail : e)));
      return updatedEmail;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update email');
      throw err;
    } finally {
      stopLoading('updateEmail');
    }
  }, [startLoading, stopLoading]);

  const deleteEmail = useCallback(async (id: number) => {
    startLoading('deleteEmail', 'Deleting email...');
    try {
      await residentApi.deleteEmail(id);
      setEmails(prev => prev.filter(e => e.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete email');
      throw err;
    } finally {
      stopLoading('deleteEmail');
    }
  }, [startLoading, stopLoading]);

  const fetchAddresses = useCallback(async () => {
    startLoading('fetchAddresses', 'Loading addresses...');
    try {
      const data = await residentApi.getAddresses();
      setAddresses(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch addresses');
    } finally {
      stopLoading('fetchAddresses');
    }
  }, [startLoading, stopLoading]);

  const createAddress = useCallback(async (addressData: CreateAddressRequest) => {
    startLoading('createAddress', 'Adding address...');
    try {
      const result = await residentApi.createAddress({
        typeId: addressData.typeId,
        address: addressData.address,
        description: addressData.description,
        isPrimary: Boolean(addressData.isPrimary)
      });
      const newAddress = result.data;
      setAddresses(prev => [...prev, newAddress]);
      return newAddress;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add address');
      throw err;
    } finally {
      stopLoading('createAddress');
    }
  }, [startLoading, stopLoading]);

  const updateAddress = useCallback(async (id: number, addressData: UpdateAddressRequest) => {
    startLoading('updateAddress', 'Updating address...');
    try {
      const result = await residentApi.updateAddress(id, addressData);
      const updatedAddress = result.data;
      setAddresses(prev => prev.map(a => (a.id === id ? updatedAddress : a)));
      return updatedAddress;
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update address');
      throw err;
    } finally {
      stopLoading('updateAddress');
    }
  }, [startLoading, stopLoading]);

  const deleteAddress = useCallback(async (id: number) => {
    startLoading('deleteAddress', 'Deleting address...');
    try {
      await residentApi.deleteAddress(id);
      setAddresses(prev => prev.filter(a => a.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete address');
      throw err;
    } finally {
      stopLoading('deleteAddress');
    }
  }, [startLoading, stopLoading]);

  return {
    property,
    lease,
    contact,
    phones,
    emails,
    addresses,
    error,
    isLoading: globalLoading,
    loadingMessage,
    fetchProperty,
    fetchLease,
    fetchContact,
    fetchPhones,
    createPhone,
    updatePhone,
    deletePhone,
    fetchEmails,
    createEmail,
    updateEmail,
    deleteEmail,
    fetchAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
  };
};
